import React, { useState } from "react";
import { Icon, Typography, makeStyles } from "@material-ui/core";
import { Box, Collapse, IconButton } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { DisplaySection } from "app/main/utils/uiUtils";
import { formatCurrency } from "app/main/utils/tableUtils";

const useStyles = makeStyles((theme) => ({
	dataRow: {
		display: "flex",
		flexDirection: "row",
		borderBottom: "1px solid",
		borderColor: theme.palette.divider,
	},
	label: {
		flex: 1,
		textAlign: "left",
		marginTop: "10px",
	},
	value: {
		textAlign: "right",
		marginTop: "10px",
		fontWeight: 600,
		whiteSpace: "nowrap",
	},
	good: {
		color: theme.palette.success.main,
	},
	bad: {
		color: theme.palette.error.main,
	},
	normal: {
		color: theme.palette.primary.dark,
	},
	recommended: {
		color: theme.palette.success.main,
		fontWeight: 700,
	},
}));

function LoadRelaySummaryView({ loadToUse, variant }) {
	const classes = useStyles();
	const [isExpanded, setIsExpanded] = useState(false);

	// Check if relay data is available
	if (!loadToUse || (!loadToUse.relay_best && !loadToUse.relay_profit && !loadToUse.relay_pickup_cost)) {
		return null;
	}

	const renderDataItem = (label, value, type, info) => {
		return (
			<div className={"flex flex-row border-b-1 border-grey-300 "}>
				<Typography component={"div"} className="flex w-full text-black text-12 ml:text-13 break-normal mt-10">
					{label}
				</Typography>

				<Typography
					component={"div"}
					className={`${classes.value} text-12 ml:text-13 
						${
							type === "BAD"
								? classes.bad
								: type === "GOOD"
								? classes.good
								: type === "RECOMMENDED"
								? classes.recommended
								: classes.normal
						}`}
				>
					{info}
					{value === null || value === undefined ? "-" : value}
				</Typography>
			</div>
		);
	};

	const renderRelayDetails = () => {
		return (
			<>
				{loadToUse.relay_best !== undefined &&
					renderDataItem(
						"Recommended Mode",
						loadToUse.relay_best ? "Relay" : "Direct",
						loadToUse.relay_best ? "RECOMMENDED" : "NORMAL",
						loadToUse.relay_best && loadToUse.relay_schedule_fits ? (
							<Icon className="text-13 text-green-600 mr-4">check_circle</Icon>
						) : (
							<Icon className="text-13 mr-4">question_mark</Icon>
						)
					)}

				{loadToUse.relay_profit !== undefined &&
					renderDataItem(
						"Relay Profit",
						formatCurrency(loadToUse.relay_profit, 0),
						loadToUse.relay_profit > 0 ? "GOOD" : loadToUse.relay_profit < 0 ? "BAD" : "NORMAL"
					)}
			</>
		);
	};

	const renderCostBreakdown = () => {
		return (
			<>
				{loadToUse.relay_pickup_cost !== undefined &&
					renderDataItem("Pickup Cost", formatCurrency(loadToUse.relay_pickup_cost, 0), "NORMAL")}

				{loadToUse.relay_delivery_cost !== undefined &&
					renderDataItem("Delivery Cost", formatCurrency(loadToUse.relay_delivery_cost, 0), "NORMAL")}

				{loadToUse.relay_long_haul_cost !== undefined &&
					renderDataItem("Long Haul Cost", formatCurrency(loadToUse.relay_long_haul_cost, 0), "NORMAL")}

				{loadToUse.relay_pickup_cost !== undefined &&
					loadToUse.relay_delivery_cost !== undefined &&
					loadToUse.relay_long_haul_cost !== undefined &&
					renderDataItem(
						"Total Relay Cost",
						formatCurrency(
							loadToUse.relay_pickup_cost + loadToUse.relay_delivery_cost + loadToUse.relay_long_haul_cost,
							0
						),
						"NORMAL"
					)}

				{loadToUse.relay_duration !== undefined && renderDataItem("Relay Duration", loadToUse.relay_duration, "NORMAL")}
			</>
		);
	};

	const getTitle = () => {
		if (loadToUse.relay_best) {
			return (
				<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
					{loadToUse.relay_schedule_fits ? (
						<Icon className="text-16 text-green-600">check_circle</Icon>
					) : (
						<Icon className="text-16">hourglass_top</Icon>
					)}
					<Typography variant="subtitle">Relay Analysis</Typography>
				</Box>
			);
		}
		return <Typography variant="subtitle">Relay Analysis</Typography>;
	};

	return (
		<DisplaySection
			title={
				<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
					{getTitle()}
					<IconButton size="small" onClick={() => setIsExpanded(!isExpanded)} sx={{ p: 0 }}>
						{isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
					</IconButton>
				</Box>
			}
			classes={{ root: "mx-0 mt-0" }}
		>
			<Collapse in={isExpanded}>
				<div className={`flex flex-col ${variant === "skinny" ? "px-2" : "px-4"} py-2`}>
					{renderRelayDetails()}
					{renderCostBreakdown()}
				</div>
			</Collapse>
		</DisplaySection>
	);
}

export default LoadRelaySummaryView;
